import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Icon } from '@/components/Icon'
import { search, type SearchHit } from '@/features/search/api'
import type { ApiError } from '@/lib/api/client'

const DESTINATION: Record<SearchHit['type'], (hit: SearchHit) => string> = {
  work_item: (hit) => `/work-items/${hit.key ?? hit.id}`,
  project: (hit) => `/projects/${hit.key ?? hit.id}`,
  client: () => '/clients',
}

const ICON = { work_item: 'list', project: 'folder', client: 'people' } as const

/**
 * Search from the sidebar, the way a work tool is used: type a key, press
 * enter, land on the thing.
 *
 * The query runs as the person typing, so what comes back is bounded by their
 * reach — typing another division's project key returns nothing rather than
 * confirming it exists.
 */
export function SearchBox() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(term.trim()), 200)

    return () => window.clearTimeout(id)
  }, [term])

  // Clicking anywhere else puts the results away.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (box.current !== null && !box.current.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)

    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  const hits = useQuery<SearchHit[], ApiError>({
    queryKey: ['search', debounced],
    queryFn: () => search(debounced),
    enabled: debounced.length >= 2,
    retry: false,
  })

  const results = hits.data ?? []

  function go(hit: SearchHit) {
    setOpen(false)
    setTerm('')
    void navigate(DESTINATION[hit.type](hit))
  }

  return (
    <div className="relative px-3 pb-2" ref={box}>
      <label className="relative block">
        <span className="text-ink-500 pointer-events-none absolute inset-y-0 start-2.5 flex items-center">
          <Icon name="search" className="size-3.5" />
        </span>
        <input
          type="search"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && results[0] !== undefined) go(results[0])
            if (event.key === 'Escape') setOpen(false)
          }}
          placeholder={t('nav.searchPlaceholder')}
          className="border-line bg-surface-2 focus:bg-surface placeholder:text-ink-500 w-full rounded-md border py-1.5 ps-8 pe-2 text-xs outline-none focus:border-[color:var(--color-brand-300)]"
        />
      </label>

      {open && debounced.length >= 2 ? (
        <div className="border-line bg-surface absolute inset-x-3 top-full z-30 mt-1 overflow-hidden rounded-md border shadow-lg">
          {results.length === 0 ? (
            <p className="text-ink-500 px-3 py-2.5 text-xs">
              {hits.isPending ? t('common.loading') : t('nav.noResults')}
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {results.map((hit) => (
                <li key={`${hit.type}-${hit.id}`}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => go(hit)}
                    className="hover:bg-surface-2 flex w-full items-center gap-2 px-3 py-2 text-start"
                  >
                    <Icon name={ICON[hit.type]} className="text-ink-500 size-3.5" />
                    <span className="min-w-0 flex-1">
                      <span className="text-ink-900 block truncate text-xs">{hit.title}</span>
                      {hit.subtitle === null ? null : (
                        <span className="text-ink-500 block truncate text-[10px]">
                          {hit.subtitle}
                        </span>
                      )}
                    </span>
                    {hit.key === null ? null : (
                      <span className="text-ink-500 font-mono text-[10px]">{hit.key}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}
